import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import api, { accountApi } from '../../services/api';
import BreadcrumbDropdown from '../atoms/BreadcrumbDropdown/BreadcrumbDropdown';
import './PageLayout.css';

/**
 * PageLayout - Unified layout for all pages with consistent breadcrumb positioning
 * 
 * @param {Array} breadcrumbs - Array of breadcrumb items: [{ label, path }]
 * @param {ReactNode} actions - Action buttons to show on the right (optional)
 * @param {Function} onCollectionSelect - Callback when a collection is selected (optional)
 * @param {ReactNode} children - Page content
 */
const PageLayout = ({ breadcrumbs = [], actions = null, headerLeft = null, inviteAction = null, onCollectionSelect = null, executions = [], onFetchExecutions = null, projectId = null, tickets = null, onFetchTickets = null, pipelines = null, onFetchPipelines = null, apps = null, onFetchApps = null, className = '', children }) => {
  const navigate = useNavigate();
  const params = useParams();
  const isInsideProject = !!params.projectId;
  const [portalTarget, setPortalTarget] = useState(null);
  const [showProjectsDropdown, setShowProjectsDropdown] = useState(false);
  const [showCollectionsDropdown, setShowCollectionsDropdown] = useState(false);
  const [projects, setProjects] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [loadingExecutions, setLoadingExecutions] = useState(false);

  const [actionsPortalTarget, setActionsPortalTarget] = useState(null);

  useEffect(() => {
    if (isInsideProject) {
      setPortalTarget(document.getElementById('header-breadcrumb-portal'));
      setActionsPortalTarget(document.getElementById('header-actions-portal'));
    } else {
      setPortalTarget(null);
      setActionsPortalTarget(null);
    }
  }, [isInsideProject]);

  const fetchProjects = async () => {
    if (projects.length > 0) return;
    
    setLoadingProjects(true);
    try {
      const data = await api.get('/projects');
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleProjectsHover = () => {
    if (!showProjectsDropdown) {
      fetchProjects();
      setShowProjectsDropdown(true);
    }
  };

  const handleProjectsClick = (path) => {
    setShowProjectsDropdown(false);
    navigate(path);
  };

  const fetchCollections = async (projectId) => {
    if (collections.length > 0) return;
    
    setLoadingCollections(true);
    try {
      const data = await accountApi.get(`/collections?projectId=${projectId}`);
      setCollections(data.collections || []);
    } catch (error) {
      console.error('Error fetching collections:', error);
    } finally {
      setLoadingCollections(false);
    }
  };

  const handleCollectionsHover = (projectId) => {
    if (!showCollectionsDropdown) {
      fetchCollections(projectId);
      setShowCollectionsDropdown(true);
    }
  };

  const handleCollectionsClick = (collectionId) => {
    setShowCollectionsDropdown(false);
    if (onCollectionSelect) {
      onCollectionSelect(collectionId);
    }
  };

  const handleExecutionsHover = (collectionId) => {
    if (onFetchExecutions) {
      setLoadingExecutions(true);
      onFetchExecutions(collectionId).finally(() => setLoadingExecutions(false));
    }
  };

  const handleExecutionClick = (executionId) => {
    navigate(`/projects/${projectId}/runs/${executionId}`);
  };

  const breadcrumbContent = (
    <div className="breadcrumb-notion">
      {breadcrumbs.map((crumb, index) => {
        const isLast = index === breadcrumbs.length - 1;
        const isProjects = crumb.label === 'Projects' && crumb.path === '/projects';
        const isProject = crumb.projectId;
        const isTicketsPage = crumb.isTicketsPage;
        const isPipelinesPage = crumb.isPipelinesPage;
        const isAppsPage = crumb.isAppsPage;
        const isCollectionWithExecutions = crumb.isCollectionWithExecutions;
        
        return (
          <React.Fragment key={index}>
            {isProjects ? (
              <BreadcrumbDropdown
                label={crumb.label}
                isActive={isLast}
                onFetch={handleProjectsHover}
                items={projects}
                loading={loadingProjects}
                onItemClick={(project) => handleProjectsClick(`/projects/${project.projectId}/pipelines`)}
                onLabelClick={() => handleProjectsClick(crumb.path)}
                getItemLabel={(project) => project.name}
                getItemKey={(project) => project.projectId}
                emptyMessage="No projects"
                headerLabel="Projects"
              />
            ) : isProject && isPipelinesPage ? (
              <BreadcrumbDropdown
                label={crumb.label}
                isActive={isLast}
                onFetch={() => {
                  if (onFetchPipelines) {
                    onFetchPipelines();
                  }
                }}
                items={pipelines || []}
                loading={false}
                onItemClick={(p) => {
                  navigate(`/projects/${crumb.projectId}/pipelines/${p.workflowType}`);
                }}
                onLabelClick={() => {
                  navigate(`/projects/${crumb.projectId}/pipelines`);
                }}
                getItemLabel={(p) => p.workflowType}
                getItemKey={(p) => p.workflowType}
                emptyMessage="No agents"
                headerLabel="Agents"
                isProject={true}
              />
            ) : isProject && isAppsPage ? (
              // Apps dropdown — same shape as the Pipelines branch above.
              // We navigate to /apps/:instanceId (NOT a slug) because apps
              // are keyed by opaque UUIDs, not human names. The dropdown
              // label uses each app's display name (with appType fallback)
              // so the visible list is human-readable even though the
              // route uses ids.
              <BreadcrumbDropdown
                label={crumb.label}
                isActive={isLast}
                onFetch={() => {
                  if (onFetchApps) {
                    onFetchApps();
                  }
                }}
                items={apps || []}
                loading={false}
                onItemClick={(a) => {
                  navigate(`/projects/${crumb.projectId}/apps/${a.instanceId}`);
                }}
                onLabelClick={() => {
                  navigate(`/projects/${crumb.projectId}/apps`);
                }}
                getItemLabel={(a) => a.name || a.appType || a.instanceId}
                getItemKey={(a) => a.instanceId}
                emptyMessage="No apps"
                headerLabel="Apps"
                isProject={true}
              />
            ) : isProject && isTicketsPage ? (
              <BreadcrumbDropdown
                label={crumb.label}
                isActive={isLast}
                onFetch={() => {
                  if (onFetchTickets) {
                    onFetchTickets();
                  }
                }}
                items={tickets || []}
                loading={false}
                onItemClick={(ticket) => {
                  if (projectId) {
                    navigate(`/projects/${projectId}/tickets/${ticket.key}`);
                  } else {
                    const element = document.querySelector(`[data-ticket-key="${ticket.key}"]`);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      element.style.backgroundColor = 'var(--ion-color-primary-tint)';
                      setTimeout(() => {
                        element.style.backgroundColor = '';
                      }, 2000);
                    }
                  }
                }}
                onLabelClick={() => {
                  const ticketsPath = `/projects/${crumb.projectId}/tickets`;
                  navigate(ticketsPath);
                }}
                getItemLabel={(ticket) => `${ticket.key}: ${ticket.summary}`}
                getItemKey={(ticket) => ticket.key}
                emptyMessage="No tickets"
                headerLabel="Tickets"
                isProject={true}
              />
            ) : isProject ? (
              <BreadcrumbDropdown
                label={crumb.label}
                isActive={isLast}
                onFetch={() => handleCollectionsHover(crumb.projectId)}
                items={collections}
                loading={loadingCollections}
                onItemClick={(collection) => handleCollectionsClick(collection.collectionId)}
                onLabelClick={() => {
                  if (onCollectionSelect) {
                    onCollectionSelect(null);
                  }
                  if (crumb.path) {
                    navigate(crumb.path);
                  }
                }}
                getItemLabel={(collection) => collection.collectionName || collection.name}
                getItemKey={(collection) => collection.collectionId}
                emptyMessage="No collections"
                headerLabel="Collections"
                isProject={true}
              />
            ) : isCollectionWithExecutions ? (
              <BreadcrumbDropdown
                label={crumb.label}
                isActive={isLast}
                onFetch={() => handleExecutionsHover(crumb.collectionId)}
                items={executions}
                loading={loadingExecutions}
                onItemClick={(execution) => handleExecutionClick(execution.executionId)}
                onLabelClick={() => {
                  if (onCollectionSelect) {
                    onCollectionSelect(crumb.collectionId);
                  }
                }}
                getItemLabel={(execution) => execution.testName || execution.executionId}
                getItemKey={(execution) => execution.executionId}
                emptyMessage="No executions"
                headerLabel="Executions"
                isProject={false}
                isCollection={true}
              />
            ) : crumb.loading ? (
              <div className="breadcrumb-item inactive">
                <div className="breadcrumb-skeleton-line" />
              </div>
            ) : React.isValidElement(crumb.label) ? (
              crumb.label
            ) : crumb.path ? (
              <span 
                className={`breadcrumb-item breadcrumb-item-plain ${isLast ? 'active' : 'inactive'} ${crumb.isCollection ? 'breadcrumb-collection' : ''}`}
                onClick={() => navigate(crumb.path)}
              >
                {crumb.label}
              </span>
            ) : (
              <span className={`breadcrumb-item breadcrumb-item-plain ${isLast ? 'active' : 'inactive'} ${crumb.isCollection ? 'breadcrumb-collection' : ''}`}>
                {crumb.label}
              </span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <div className={`page-layout main-body ${className}`.trim()}>
      {/* Portal breadcrumb into header when inside a project */}
      {isInsideProject && portalTarget && ReactDOM.createPortal(breadcrumbContent, portalTarget)}
      {/* Portal only invite button into header when inside a project */}
      {isInsideProject && actionsPortalTarget && inviteAction && ReactDOM.createPortal(inviteAction, actionsPortalTarget)}

      {!isInsideProject && (
        <div className="page-header-fixed">
          <div className="header-text">
            {breadcrumbContent}
          </div>
          <div className="header-actions">
            {headerLeft && <div className="header-left">{headerLeft}</div>}
            {actions && <div className="header-right">{actions}</div>}
            {inviteAction && <div className="header-right">{inviteAction}</div>}
          </div>
        </div>
      )}

      <div className="page-content">
        {children}
      </div>
    </div>
  );
};

export default PageLayout;

